<template>
  <!-- 签到奖励弹窗 -->
  <PublicPopup
    ref="CheckInPopupRef"
    type="fullScreen"
    position="center"
    :height="100"
    :padding="0"
    :closeable="false"
    :overlay-style="overlayStyle"
    @close="handleCheckInPopupClose"
  >
    <template #content>
      <div class="seven-day-check-in">
        <div class="seven-day-check-in__scroll">
          <div class="seven-day-check-in__content">
            <CheckInReward
              ref="CheckInRewardRef"
              @open-recharge="handleOpenRecharge"
              @open-deposit="handleOpenDepositFromCheckIn"
              @sign-success="handleSignSuccess"
              @request-state-change="handleCheckInRequestStateChange"
            />

            <div class="seven-day-check-in__checkbox">
              <CheckboxPublic type="seven-day-check-in" />
            </div>

            <div
              class="seven-day-check-in__close chxy-row-c-c"
              :class="{ 'seven-day-check-in__close--disabled': isCheckInCloseDisabled }"
              :aria-disabled="isCheckInCloseDisabled"
              @click="handleCheckInClose"
            >
              <van-icon name="close" size="32" color="#FFFFFF" />
            </div>
          </div>
        </div>
      </div>
    </template>
  </PublicPopup>

  <!-- 充值签收弹窗 -->
  <PublicPopup
    ref="RechargePopupRef"
    type="fullScreen"
    position="center"
    :height="100"
    :padding="0"
    :closeable="false"
    :overlay-style="overlayStyle"
    @close="handleRechargePopupClose"
  >
    <template #content>
      <div class="seven-day-check-in seven-day-check-in--recharge">
        <div class="seven-day-check-in__scroll">
          <div class="seven-day-check-in__content">
            <RechargeClaim
              :type="rechargePayload.type"
              :total-bonus="rechargePayload.totalBonus"
              :deposit-amount="rechargePayload.depositAmount"
              :deposit-loading="isRechargeLoading"
              @close="handleRechargeClose"
              @deposit="handleRechargeDeposit"
              @go-home="handleRechargeHome"
            />
          </div>
        </div>
      </div>
    </template>
  </PublicPopup>

  <DepositoPayPopup ref="DepositoPayPopupRef" :pay-info="payInfo" @close="handleDepositoPayClose" />
</template>

<script setup>
import CheckInReward from './components/CheckInReward/CheckInReward.vue'
import RechargeClaim from './components/RechargeClaim/RechargeClaim.vue'
import DepositoPayPopup from '@/pages/deposito/components/DepositoPayPopup.vue'
import { cycleSignGetInfoApi, checkInRechargeStartApi } from '@/api/checkInReward'
import { rechargeQueryOrderInfoApi } from '@/api/deposito'
import { isUnlockOrderFailed, isUnlockOrderSuccess, mapCheckInInfo } from './js/mapper.js'
import { RECHARGE_CLAIM_TYPE } from './components/RechargeClaim/js/index.js'
import {
  applyRechargeClaimPayload,
  createRedeemedOpenPayload,
  createRechargeOpenPayload,
} from './components/RechargeClaim/js/open.js'

/** localStorage 键 — 「今日不再展示」 */
const STORAGE_KEY = 'SevenDayCheckInPopupChecked'
/** 普通签到成功后关闭延迟（ms） */
const SIGN_CLOSE_DELAY = 1500
/** 支付状态轮询间隔（ms） */
const PAY_STATUS_POLL_INTERVAL = 3000

/** PublicPopup 遮罩样式 */
const overlayStyle = {
  background: 'rgba(0, 0, 0, 0.65)',
}

const route = useRoute()
const router = useRouter()

/** 签到弹窗 ref */
const CheckInPopupRef = ref(null)
/** 充值签收弹窗 ref */
const RechargePopupRef = ref(null)
/** 签到内容组件 ref */
const CheckInRewardRef = ref(null)
/** 支付弹窗 ref */
const DepositoPayPopupRef = ref(null)

/** 是否勾选「今日不再展示」 */
const popupChecked = ref(window.localStorage[STORAGE_KEY] || 'null')

/** 充值签收弹窗传入数据 */
const rechargePayload = reactive({
  type: RECHARGE_CLAIM_TYPE.RECHARGE, // 弹窗类型 recharge / redeemed
  totalBonus: 0, // 累计奖励金额
  depositAmount: 0, // 解锁充值金额
})

/** 支付弹窗订单信息 */
const payInfo = ref({})
/** 创建充值订单 loading */
const isRechargeLoading = ref(false)
/** 签到接口请求中 */
const isCheckInRequestLoading = ref(false)
/** 签到弹窗关闭图标是否禁用 */
const isCheckInCloseDisabled = computed(
  () => isCheckInRequestLoading.value || isRechargeLoading.value,
)
/** 解锁前储蓄罐金额（签收成功弹窗展示用） */
const lastUnlockReward = ref(0)
/** 是否正在等待支付解锁结果 */
const pendingUnlockPay = ref(false)
/** 支付结果是否已处理（防重复） */
const payResultHandled = ref(false)

/** 支付状态轮询定时器 */
let payPollTimer = null
/** 签到成功延迟关闭定时器 */
let signCloseTimer = null
/** 正在打开签到弹窗（防并发重复 open） */
let openInFlight = null

/**
 * 流程内切换弹窗时抑制 close 上报。
 * 抑制标志在整次切换期间保持，等下一层弹窗打开后再清除，
 * 避免同一次切换里多次 close 误触发下载弹窗。
 * 只有整条签到闭环真正结束时，才向 App emit('close')。
 */
const suppressCloseEmit = ref(false)

const emit = defineEmits(['close', 'recharge-claim-open', 'redeemed-claim-open'])

/** 停止支付状态轮询 */
const stopPayStatusPoll = () => {
  if (payPollTimer) {
    clearInterval(payPollTimer)
    payPollTimer = null
  }
}

/** 清除签到成功延迟关闭定时器 */
const clearSignCloseTimer = () => {
  if (signCloseTimer) {
    clearTimeout(signCloseTimer)
    signCloseTimer = null
  }
}

/** 开始流程内切换：后续 close 不向 App 上报 */
const markSuppressClose = () => {
  suppressCloseEmit.value = true
}

/** 结束流程内切换 */
const clearSuppressClose = () => {
  suppressCloseEmit.value = false
}

/** 签到闭环完成 — 通知 App 继续下载弹窗 */
const emitFlowComplete = () => {
  suppressCloseEmit.value = false
  emit('close')
}

/**
 * 处理弹窗关闭：流程内切换则吞掉，否则上报闭环完成
 * @returns {boolean} 是否已上报闭环完成
 */
const handleFlowPopupClose = () => {
  if (suppressCloseEmit.value) return false
  emitFlowComplete()
  return true
}

/** 打开支付弹窗并启动轮询 */
const openDepositoPayPopup = () => {
  nextTick(() => {
    DepositoPayPopupRef.value?.open()
    // startPayStatusPoll()
    // 支付弹窗已打开，允许后续用户关闭时结束链路
    clearSuppressClose()
  })
}

/** 轮询充值订单状态：成功打开签收弹窗，失败 Toast 并保留支付弹窗 */
const startPayStatusPoll = () => {
  stopPayStatusPoll()

  payPollTimer = setInterval(async () => {
    const orderNo = payInfo.value?.merchantOrderId
    if (!orderNo || payResultHandled.value) return

    try {
      const { code, data, msg } = await rechargeQueryOrderInfoApi({ order_no: orderNo })
      if (code != 1) return

      const status = data?.status

      if (isUnlockOrderSuccess(status)) {
        payResultHandled.value = true
        stopPayStatusPoll()
        pendingUnlockPay.value = false
        // 支付成功 → 签收成功弹窗，由 openRedeemedClaim 统一处理切换
        openRedeemedPopup({ totalBonus: lastUnlockReward.value })
        return
      }

      if (isUnlockOrderFailed(status)) {
        stopPayStatusPoll()
        showToast(msg || 'Pagamento falhou')
      }
    } catch (error) {
      console.error('[SevenDayCheckIn] pay status poll error:', error)
    }
  }, PAY_STATUS_POLL_INTERVAL)
}

/**
 * 创建充值订单并打开支付弹窗
 * @param {{ depositAmount: number, totalBonus?: number }} payload
 * @param {{ closeCheckIn?: boolean, closeRecharge?: boolean }} options
 */
const createDepositOrderAndPay = async (payload, options = {}) => {
  const { closeCheckIn = false, closeRecharge = false } = options // 是否关闭签到/签收弹窗

  if (isRechargeLoading.value) return false

  lastUnlockReward.value = payload.totalBonus ?? lastUnlockReward.value
  isRechargeLoading.value = true

  try {
    const { code, data, msg } = await checkInRechargeStartApi({
      amount: Number(payload.depositAmount),
      recharge_scene: 'cycle_sign_unlock',
    })

    if (code == 1) {
      payInfo.value = data
      pendingUnlockPay.value = true
      payResultHandled.value = false

      // 关闭签到/签收弹窗以打开支付弹窗，属于流程内切换
      if (closeCheckIn || closeRecharge) markSuppressClose()
      if (closeCheckIn) CheckInPopupRef.value?.close()
      if (closeRecharge) RechargePopupRef.value?.close()

      openDepositoPayPopup()
      return true
    }

    if (msg) showToast(msg)
    return false
  } catch (error) {
    console.error('[SevenDayCheckIn] rechargeStart error:', error)
    return false
  } finally {
    isRechargeLoading.value = false
  }
}

/**
 * 打开签到弹窗
 * @param {{ manual?: boolean }} options manual 为 true 时表示用户主动打开，
 * 不受自动弹窗的页面、今日不再展示和活动状态限制
 */
const open = async (options = {}) => {
  const { manual = false } = options

  // 并发调用复用同一次 open，避免竞态下一次成功、一次失败同时弹下载
  if (openInFlight) return openInFlight

  openInFlight = (async () => {
    if (!manual && popupChecked.value === 'checked') return false
    if (!manual && route.name !== 'Home') return false

    let rawData = null
    try {
      const { code, data } = await cycleSignGetInfoApi()
      if (code == 1) rawData = data
    } catch (error) {
      console.error('[SevenDayCheckIn] getInfo error:', error)
      return false
    }

    const info = mapCheckInInfo(rawData || {})

    if (!manual && !info.enabled) return false

    // 新开签到链路，重置切换抑制状态
    clearSuppressClose()
    clearSignCloseTimer()
    // 组件在退出登录后仍保持挂载，必须清除上一次第 7 天流程遗留的 loading
    CheckInRewardRef.value?.stopNeonLoading?.()
    CheckInPopupRef.value?.open()

    await nextTick()
    CheckInRewardRef.value?.applyCheckInInfo(rawData)

    return true
  })()

  try {
    return await openInFlight
  } finally {
    openInFlight = null
  }
}

/** 打开充值签收弹窗（recharge）— 供外部 / 内部调用 */
const openRechargeClaim = (payload = {}) => {
  clearSignCloseTimer()
  const openPayload = createRechargeOpenPayload(payload)
  lastUnlockReward.value = openPayload.totalBonus
  applyRechargeClaimPayload(rechargePayload, openPayload)

  // 签到弹窗 → 充值签收弹窗，属于流程内切换
  markSuppressClose()
  CheckInPopupRef.value?.close()

  nextTick(() => {
    RechargePopupRef.value?.open()
    clearSuppressClose()
    emit('recharge-claim-open', { ...rechargePayload })
  })
}

/** 打开充值成功弹窗（redeemed）— 供外部 / 内部调用 */
// payload 改成 messageBody
const openRedeemedClaim = (messageBody = {}) => {
  // 原方式   ??? 参数方式不一致 ???
  // const openPayload = createRedeemedOpenPayload(payload)
  // applyRechargeClaimPayload(rechargePayload, openPayload)

  // 新补参   充值成功弹窗 - 弹窗信息是ws 返回的（仅签到充值成功）
  if (
    (messageBody && messageBody?.biz_type == 1 && messageBody?.status == 3) ||
    messageBody?.status == 1
  ) {
    rechargePayload.type = 'redeemed'
    rechargePayload.totalBonus = messageBody?.cycle_sign_reward_amount || '0,00' // 实际是 签到奖励金额
    rechargePayload.depositAmount = messageBody?.cycle_sign_reward_amount || '0,00' // 实际是  签到奖励金额

    // 关闭签到/支付弹窗以打开签收成功弹窗，属于流程内切换
    markSuppressClose()
    CheckInPopupRef.value?.close()
    DepositoPayPopupRef.value?.close()

    nextTick(() => {
      RechargePopupRef.value?.open()
      clearSuppressClose()
      emit('redeemed-claim-open', { ...rechargePayload })
    })
  }
}

/** 关闭充值签收 / 成功弹窗 — 供外部调用 */
const closeRechargeClaim = () => {
  RechargePopupRef.value?.close()
}

/**
 * 打开充值签收弹窗（recharge），未传参时从 getInfo 拉取
 * @param {{ totalBonus?: number, depositAmount?: number }} payload
 */
const openRechargeClaimWithInfo = async (payload = {}) => {
  if (payload.totalBonus !== undefined || payload.depositAmount !== undefined) {
    openRechargeClaim(payload)
    return true
  }

  try {
    const { code, data } = await cycleSignGetInfoApi()
    if (code != 1) return false

    const info = mapCheckInInfo(data)
    openRechargeClaim({
      totalBonus: info.piggyAmount || info.totalReward,
      depositAmount: info.unlockRechargeAmount,
    })
    return true
  } catch (error) {
    console.error('[SevenDayCheckIn] openRechargeClaimWithInfo error:', error)
    return false
  }
}

/** 打开充值成功弹窗（redeemed） */
const openRedeemedPopup = (payload = {}) => {
  openRedeemedClaim(payload)
}

/** 关闭签到弹窗 — 关闭按钮（用户主动结束链路） */
const handleCheckInClose = () => {
  if (isCheckInCloseDisabled.value) return
  clearSignCloseTimer()
  suppressCloseEmit.value = false
  CheckInPopupRef.value?.close()
}

/** 同步签到接口请求状态，请求返回或超时后恢复关闭能力 */
const handleCheckInRequestStateChange = (loading) => {
  isCheckInRequestLoading.value = loading
}

/** 签到弹窗关闭回调 */
const handleCheckInPopupClose = () => {
  clearSignCloseTimer()
  // PublicPopup 关闭不会卸载签到组件，关闭时同步清理临时交互状态
  CheckInRewardRef.value?.stopNeonLoading?.()
  handleFlowPopupClose()
}

/** 第 7 天签到成功 — 打开充值签收弹窗（recharge），不结束链路 */
const handleOpenRecharge = (payload) => {
  openRechargeClaim(payload)
}

/** 满 7 天锁定态 — 签到弹窗主按钮直接打开支付弹窗，不结束链路 */
const handleOpenDepositFromCheckIn = async (payload) => {
  const ok = await createDepositOrderAndPay(payload, { closeCheckIn: true })
  // 创建订单失败时签到弹窗仍在，结束霓虹 loading
  if (!ok) CheckInRewardRef.value?.stopNeonLoading?.()
}

/** 普通签到成功 — 延迟 1.5s 关闭签到弹窗，关闭后结束链路 */
const handleSignSuccess = () => {
  clearSignCloseTimer()
  suppressCloseEmit.value = false
  signCloseTimer = setTimeout(() => {
    CheckInPopupRef.value?.close()
    signCloseTimer = null
  }, SIGN_CLOSE_DELAY)
}

/** 充值签收弹窗 — 底部关闭（结束链路） */
const handleRechargeClose = () => {
  if (isRechargeLoading.value) return
  suppressCloseEmit.value = false
  RechargePopupRef.value?.close()
}

/** 充值签收弹窗关闭回调 */
const handleRechargePopupClose = () => {
  handleFlowPopupClose()
}

/** 充值签收弹窗主按钮 — 打开支付弹窗并关闭当前弹窗，不结束链路 */
const handleRechargeDeposit = async (payload) => {
  await createDepositOrderAndPay(payload, { closeRecharge: true })
}

/** 签收成功主按钮 — 非首页则跳转首页，并关闭弹窗（结束链路） */
const handleRechargeHome = () => {
  suppressCloseEmit.value = false
  RechargePopupRef.value?.close()
  if (route.name !== 'Home') {
    router.push({ name: 'Home' })
  }
}

/**
 * 支付弹窗关闭：
 * - 若是切到签收成功弹窗：不结束链路
 * - 若用户关闭 / 支付结束且无后续弹窗：结束链路，通知 App 弹下载
 */
const handleDepositoPayClose = () => {
  stopPayStatusPoll()
  pendingUnlockPay.value = false
  payResultHandled.value = false
  handleFlowPopupClose()
}

onBeforeUnmount(() => {
  stopPayStatusPoll()
  clearSignCloseTimer()
})

defineExpose({
  /** 打开签到弹窗 */
  open,
  /** 打开充值签收弹窗（recharge） */
  openRechargeClaim,
  /** 打开充值签收弹窗并自动拉取 getInfo */
  openRechargeClaimWithInfo,
  /** 打开充值成功弹窗（redeemed） */
  openRedeemedClaim,
  /** 关闭充值签收 / 成功弹窗 */
  closeRechargeClaim,
})
</script>

<style lang="less" scoped>
.seven-day-check-in {
  position: relative;
  width: 100%;
  height: 100dvh;
  max-height: 100dvh;
  overflow: hidden;
  pointer-events: none;

  &__scroll {
    position: relative;
    width: 100%;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: max(12px, env(safe-area-inset-top)) 0 max(28px, env(safe-area-inset-bottom));
    pointer-events: auto;
  }

  &__content {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: min(375px, 100vw);
    display: flex;
    flex-direction: column;
    align-items: center;
    pointer-events: auto;
  }

  &__checkbox {
    position: relative;
    width: 100%;
    margin-top: 10px;
    z-index: 10;
    pointer-events: auto;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;

    :deep(.checkbox-container) {
      cursor: pointer;
    }

    :deep(.checkbox) {
      cursor: pointer;
    }
  }

  &__close {
    position: relative;
    width: 44px;
    height: 44px;
    margin-top: 14px;
    margin-bottom: 18px;
    z-index: 10;
    pointer-events: auto;
    cursor: pointer;

    &--disabled {
      pointer-events: none;
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
}
</style>
